import { createFileRoute, useLoaderData } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { SectionPage } from "@/components/SectionPage";
import { RelatedHubs } from "@/components/RelatedHubs";
import { SeoItemList } from "@/components/SeoItemList";
import { getSeoSectionItems } from "@/lib/seo.functions";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";


export const Route = createFileRoute("/eat")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { q?: string; page?: number; lane?: string; r?: string; city?: string; tag?: string } => ({
    q: typeof search.q === "string" ? search.q : undefined,
    page: Number(search.page) > 0 ? Number(search.page) : 1,
    lane: typeof search.lane === "string" ? search.lane : undefined,
    r: typeof search.r === "string" ? search.r : undefined,
    city: typeof search.city === "string" ? search.city : undefined,
    tag: typeof search.tag === "string" ? search.tag : undefined,
  }),
  loader: async () => {
    try {
      const { items } = await getSeoSectionItems({ data: { section: "eat", limit: 30 } });
      return { items };
    } catch {
      return { items: [] };
    }
  },
  head: ({ loaderData }) => {
    const items = loaderData?.items ?? [];
    const url = "https://whatfinland.com/eat";
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "What to eat in Finland — curated restaurants, cafes & bars",
      url,
      numberOfItems: items.length,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://whatfinland.com/place/${it.slug}`,
        name: it.name,
      })),
    };
    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://whatfinland.com/" },
        { "@type": "ListItem", position: 2, name: "Eat", item: url },
      ],
    };
    return {
      meta: [
        { title: "What to Eat in Helsinki — Curated Restaurants, Cafes & Bars" },
        { name: "description", content: "Curated restaurants, cafes, bars, and hidden gems in Helsinki — handpicked by what Finland Oy." },
        { property: "og:title", content: "What to Eat — Helsinki" },
        { property: "og:description", content: "The tables, cafes, and bars worth your time in Helsinki." },
        { property: "og:url", content: url },
        ...buildLocaleMeta("en"),
        { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...buildHreflangLinks("/eat"),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(itemList) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
      ],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  // `strict: false` so the /fi mirror route can reuse this component.
  const { items } = useLoaderData({ strict: false }) as any;
  return (
    <>
      <SectionPage section="eat" />
      <RelatedHubs current="eat" />
      <SeoItemList
        items={items}
        heading="All curated places to eat in Finland"
        hrefBase="/place"
      />
    </>
  );
}
